import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Crown, Filter, Download, Search as SearchIcon, Flame, TrendingUp, ArrowUp, ArrowDown, Minus } from 'lucide-react';
import { Card, CardDescription, Skeleton, Input, Button } from '@/components/ui';
import { ScoreBadge } from '@/components/keyword';
import { api } from '@/lib/api';
import { formatNumber, type GoldenKeywordItem, type GradeDistribution, type RealtimeGoldenItem } from '@keywordgod/shared';
import { cn } from '@/lib/utils';

type SortKey = 'godScore' | 'totalSearch' | 'saturation' | 'keyword';
type SortDir = 'desc' | 'asc';

interface Filters {
  q: string; // keyword contains
  minGodScore: number;
  maxSaturation: number;
  minTotalSearch: number;
}

// Phase 37: 백엔드 임계와 동기화 (god>=65, sat<30, search>=500)
const DEFAULT_FILTERS: Filters = {
  q: '',
  minGodScore: 65,
  maxSaturation: 30,
  minTotalSearch: 500,
};

type Tab = 'today' | 'realtime';

export function GoldenKeyword() {
  const [tab, setTab] = useState<Tab>('today');
  const q = useQuery({ queryKey: ['golden', 'today'], queryFn: () => api.golden.today() });

  const [filters, setFilters] = useState<Filters>(DEFAULT_FILTERS);
  const [sortKey, setSortKey] = useState<SortKey>('godScore');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  const items = q.data?.items ?? [];

  const filteredSorted = useMemo(() => {
    const lower = filters.q.trim().toLowerCase();
    const filtered = items.filter((it) => {
      if (lower && !it.keyword.toLowerCase().includes(lower)) return false;
      if (it.godScore < filters.minGodScore) return false;
      if (it.saturation > filters.maxSaturation) return false;
      if (it.totalSearch < filters.minTotalSearch) return false;
      return true;
    });
    const sorted = [...filtered].sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      if (typeof av === 'string' && typeof bv === 'string') {
        return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
      }
      const an = Number(av);
      const bn = Number(bv);
      return sortDir === 'asc' ? an - bn : bn - an;
    });
    return sorted;
  }, [items, filters, sortKey, sortDir]);

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-3xl font-extrabold flex items-center gap-2">
          <Crown className="h-7 w-7 text-gold" />
          <span className="text-gold-gradient">황금키워드</span>
        </h1>
        <p className="mt-1 text-sm text-gray-400">
          누적 풀 (매일 갱신) + 실시간 풀 (매시간 갱신)
        </p>
      </header>

      <div className="flex items-center gap-2 border-b border-gray-800">
        <button
          type="button"
          onClick={() => setTab('today')}
          className={cn(
            'inline-flex items-center gap-1.5 px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors',
            tab === 'today'
              ? 'border-gold text-gold'
              : 'border-transparent text-gray-400 hover:text-white',
          )}
        >
          <Crown className="h-4 w-4" /> 누적 황금 TOP 100
        </button>
        <button
          type="button"
          onClick={() => setTab('realtime')}
          className={cn(
            'inline-flex items-center gap-1.5 px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors',
            tab === 'realtime'
              ? 'border-orange-400 text-orange-300'
              : 'border-transparent text-gray-400 hover:text-white',
          )}
        >
          <Flame className="h-4 w-4" /> 실시간 황금 (1h 갱신)
          <span className="ml-1 rounded-full bg-orange-400/20 px-1.5 py-0.5 text-[10px] font-bold text-orange-200">
            NEW
          </span>
        </button>
      </div>

      {tab === 'realtime' && <RealtimeGoldenSection />}

      {tab === 'today' && q.data?.distribution && (
        <DistributionCard
          distribution={q.data.distribution}
          previousSnapshotAt={q.data.previousSnapshotAt ?? null}
          generatedAt={q.data.generatedAt}
        />
      )}

      {tab === 'today' && (
      <>
      <p className="text-sm text-gray-400">
        매일 자동 갱신 · godScore ≥ 65, 포화도 &lt; 30%, 월 검색량 ≥ 500
        {q.data && (
          <span className="ml-2 text-gray-500">
            · {q.data.date} 기준 {q.data.items.length}건 풀 / 필터 적용 {filteredSorted.length}건
          </span>
        )}
      </p>

      <FiltersCard
        filters={filters}
        onChange={setFilters}
        sortKey={sortKey}
        sortDir={sortDir}
        onSortKey={setSortKey}
        onSortDir={setSortDir}
        // Phase 35: CSV는 항상 전체 100개 (필터/정렬 무관). 정렬은 godScore DESC 고정.
        onExport={() => downloadCsv(items, q.data?.date)}
        canExport={items.length > 0}
        exportCount={items.length}
      />

      {q.isLoading && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} className="h-40 w-full" />
          ))}
        </div>
      )}

      {q.data && items.length === 0 && (
        <Card className="text-gray-400">
          아직 충분한 데이터가 누적되지 않았습니다. 키워드 분석을 더 쌓으면 자동으로 채워집니다.
        </Card>
      )}

      {items.length > 0 && filteredSorted.length === 0 && (
        <Card className="text-gray-400 text-center py-8">
          필터 조건과 일치하는 키워드가 없습니다. 조건을 완화해 보세요.
        </Card>
      )}

      {filteredSorted.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {filteredSorted.map((it, i) => (
            <Link key={it.keyword} to={`/analyze/${encodeURIComponent(it.keyword)}`}>
              <Card className="h-full transition-transform hover:-translate-y-0.5">
                <div className="flex items-start justify-between">
                  <span className="text-xs font-bold text-gold">#{i + 1}</span>
                  <ScoreBadge grade={it.grade} size="sm" />
                </div>
                <div className="mt-3 truncate text-base font-bold text-white">{it.keyword}</div>
                <CardDescription className="mt-2 truncate">{it.verdict}</CardDescription>
                <div className="mt-4 flex items-center justify-between text-xs text-gray-400">
                  <span className="tabular">God {it.godScore}</span>
                  <span className="tabular">월 {formatNumber(it.totalSearch)}</span>
                  <span className="tabular">포화 {it.saturation}%</span>
                </div>
              </Card>
            </Link>
          ))}
        </div>
      )}
      </>
      )}
    </div>
  );
}

// ─── Phase 52: 등급 분포 카드 ────────────────────────────────

function DistributionCard({
  distribution,
  previousSnapshotAt,
  generatedAt,
}: {
  distribution: GradeDistribution;
  previousSnapshotAt: number | null;
  generatedAt: number;
}) {
  const grades: Array<{ key: 'S' | 'A' | 'B' | 'C' | 'D'; label: string; tone: string }> = [
    { key: 'S', label: '황금', tone: 'border-amber-400/50 bg-amber-400/10 text-amber-200' },
    { key: 'A', label: '우수', tone: 'border-emerald-400/50 bg-emerald-400/10 text-emerald-200' },
    { key: 'B', label: '양호', tone: 'border-blue-400/50 bg-blue-400/10 text-blue-200' },
    { key: 'C', label: '보통', tone: 'border-gray-600 bg-gray-800/50 text-gray-300' },
    { key: 'D', label: '낮음', tone: 'border-red-500/40 bg-red-500/10 text-red-300' },
  ];
  return (
    <Card>
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <div className="text-xs uppercase tracking-widest text-gray-400">등급 분포</div>
        <div className="text-[11px] text-gray-500">
          {previousSnapshotAt
            ? `직전 스냅샷 대비 증감 · 갱신 ${new Date(generatedAt * 1000).toLocaleString('ko-KR')}`
            : `첫 스냅샷 · 갱신 ${new Date(generatedAt * 1000).toLocaleString('ko-KR')}`}
        </div>
      </div>
      <div className="grid grid-cols-5 gap-2">
        {grades.map((g) => {
          const d = distribution[g.key];
          return (
            <div key={g.key} className={cn('rounded-xl border px-3 py-3 text-center', g.tone)}>
              <div className="text-2xl font-extrabold leading-none">{g.key}</div>
              <div className="mt-1 text-[10px] uppercase tracking-widest opacity-80">{g.label}</div>
              <div className="mt-2 tabular text-2xl font-bold text-white">{d.count}</div>
              <DiffBadge diff={d.diff} />
            </div>
          );
        })}
      </div>
    </Card>
  );
}

function DiffBadge({ diff }: { diff: number }) {
  if (diff === 0) {
    return (
      <span className="mt-1 inline-flex items-center gap-0.5 text-[10px] text-gray-500">
        <Minus className="h-3 w-3" /> 0
      </span>
    );
  }
  const up = diff > 0;
  return (
    <span
      className={cn(
        'mt-1 inline-flex items-center gap-0.5 text-[10px] font-bold',
        up ? 'text-emerald-300' : 'text-red-300',
      )}
    >
      {up ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />}
      {up ? '+' : ''}
      {diff}
    </span>
  );
}

// ─── Phase 52: 실시간 황금풀 섹션 ────────────────────────────

function RealtimeGoldenSection() {
  const q = useQuery({
    queryKey: ['golden', 'realtime'],
    queryFn: () => api.golden.realtime(),
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
  const items = q.data?.items ?? [];

  return (
    <div className="space-y-4">
      <Card className="border-orange-400/30 bg-orange-400/5">
        <div className="flex items-start gap-3">
          <Flame className="h-5 w-5 shrink-0 text-orange-400 mt-0.5" />
          <div className="text-xs text-orange-100/80 leading-snug">
            <strong className="text-orange-200">실시간 황금풀</strong> — 최근 48시간 안에 분석된 키워드 + DataLab
            가속도(최근 7일 vs 이전 7일) 가중. 매시간 자동 갱신. 누적 풀과는 별개의 신선도 기반 풀입니다.
          </div>
        </div>
      </Card>

      {q.data?.distribution && (
        <DistributionCard
          distribution={q.data.distribution}
          previousSnapshotAt={q.data.previousSnapshotAt}
          generatedAt={q.data.generatedAt}
        />
      )}

      {q.data && (
        <div className="flex items-center justify-between text-xs text-gray-500 flex-wrap gap-2">
          <span>
            총 {items.length}개 · 신규{' '}
            <strong className="text-orange-300">+{q.data.newCount}</strong> · 후보 {q.data.totalScanned}개 스캔
          </span>
          <span>
            신선도 컷오프: {new Date(q.data.freshnessCutoff * 1000).toLocaleString('ko-KR')}
          </span>
        </div>
      )}

      {q.isLoading && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} className="h-32 w-full" />
          ))}
        </div>
      )}

      {q.isError && (
        <Card className="border-red-500/40 bg-red-500/10 text-red-200 text-sm">
          실시간 풀 조회 실패: {(q.error as Error).message}
        </Card>
      )}

      {items.length === 0 && q.data && (
        <Card className="text-gray-400 text-center py-8">
          최근 48시간 안에 분석된 검증 풀이 부족합니다. 시드 분석이 더 쌓이면 자동 채워집니다.
        </Card>
      )}

      {items.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {items.map((it, i) => (
            <RealtimeCard key={it.keyword} item={it} rank={i + 1} />
          ))}
        </div>
      )}
    </div>
  );
}

function RealtimeCard({ item, rank }: { item: RealtimeGoldenItem; rank: number }) {
  const accPct = item.acc != null ? Math.round(item.acc * 100) : null;
  const accColor =
    accPct == null
      ? 'text-gray-500'
      : accPct >= 50
      ? 'text-red-300'
      : accPct >= 20
      ? 'text-orange-300'
      : accPct >= 0
      ? 'text-amber-300'
      : 'text-gray-400';
  return (
    <Link to={`/analyze/${encodeURIComponent(item.keyword)}`}>
      <Card className="h-full transition-transform hover:-translate-y-0.5">
        <div className="flex items-start justify-between gap-2">
          <span className="text-xs font-bold text-gold">#{rank}</span>
          <div className="flex items-center gap-1">
            {item.isNew && (
              <span className="rounded-full bg-orange-400/20 px-1.5 py-0.5 text-[9px] font-bold text-orange-200">
                NEW
              </span>
            )}
            <ScoreBadge grade={item.grade} size="sm" />
          </div>
        </div>
        <div className="mt-3 truncate text-base font-bold text-white">{item.keyword}</div>
        <div className="mt-2 flex items-center justify-between text-xs">
          <span className="tabular text-gray-400">God {item.godScore}</span>
          <span className={cn('tabular font-bold', accColor)}>
            {accPct != null ? (
              <>
                <TrendingUp className="inline h-3 w-3 mr-0.5" />
                {accPct >= 0 ? '+' : ''}
                {accPct}%
              </>
            ) : (
              '-'
            )}
          </span>
        </div>
        <div className="mt-1.5 flex items-center justify-between text-[11px] text-gray-500">
          <span className="tabular">월 {formatNumber(item.totalSearch)}</span>
          <span className="tabular">포화 {item.saturation}%</span>
        </div>
      </Card>
    </Link>
  );
}

// ─── 필터 + 정렬 + 다운로드 카드 ──────────────────────────────

function FiltersCard({
  filters,
  onChange,
  sortKey,
  sortDir,
  onSortKey,
  onSortDir,
  onExport,
  canExport,
  exportCount,
}: {
  filters: Filters;
  onChange: (f: Filters) => void;
  sortKey: SortKey;
  sortDir: SortDir;
  onSortKey: (k: SortKey) => void;
  onSortDir: (d: SortDir) => void;
  onExport: () => void;
  canExport: boolean;
  exportCount: number;
}) {
  return (
    <Card>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-sm font-bold text-gold">
          <Filter className="h-4 w-4" /> 필터 · 정렬
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => onChange(DEFAULT_FILTERS)}
            className="text-xs text-gray-400 hover:text-gold"
          >
            기본값으로
          </button>
          <Button
            variant="secondary"
            size="sm"
            onClick={onExport}
            disabled={!canExport}
            title={`전체 ${exportCount}개 황금키워드 (godScore 정렬) 를 CSV로 다운로드. 화면 필터와 무관.`}
          >
            <Download className="h-4 w-4" />
            CSV ({exportCount}개)
          </Button>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
        <label className="block">
          <span className="text-xs text-gray-400 flex items-center gap-1">
            <SearchIcon className="h-3 w-3" /> 키워드 검색
          </span>
          <Input
            className="mt-1"
            placeholder="포함된 단어"
            value={filters.q}
            onChange={(e) => onChange({ ...filters, q: e.target.value })}
          />
        </label>

        <NumField
          label="최소 GodScore (≥)"
          value={filters.minGodScore}
          step={5}
          min={0}
          max={100}
          onChange={(v) => onChange({ ...filters, minGodScore: v })}
        />
        <NumField
          label="최대 포화도 % (≤)"
          value={filters.maxSaturation}
          step={5}
          min={0}
          max={100}
          onChange={(v) => onChange({ ...filters, maxSaturation: v })}
        />
        <NumField
          label="최소 월 검색량 (≥)"
          value={filters.minTotalSearch}
          step={500}
          min={0}
          onChange={(v) => onChange({ ...filters, minTotalSearch: v })}
        />
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <span className="text-xs text-gray-400">정렬:</span>
        {(
          [
            { k: 'godScore', label: 'GodScore' },
            { k: 'totalSearch', label: '검색량' },
            { k: 'saturation', label: '포화도' },
            { k: 'keyword', label: '키워드명' },
          ] as { k: SortKey; label: string }[]
        ).map((s) => (
          <button
            key={s.k}
            type="button"
            onClick={() => onSortKey(s.k)}
            className={
              'rounded-full border px-3 py-1 text-xs transition-colors ' +
              (sortKey === s.k
                ? 'border-gold bg-gold/10 text-gold'
                : 'border-gray-800 text-gray-400 hover:border-gold/40 hover:text-gold')
            }
          >
            {s.label}
          </button>
        ))}
        <div className="ml-auto inline-flex rounded-lg border border-gray-800 overflow-hidden text-xs">
          <button
            type="button"
            onClick={() => onSortDir('desc')}
            className={
              'px-2 py-1 ' + (sortDir === 'desc' ? 'bg-gold/15 text-gold' : 'text-gray-400 hover:text-gold')
            }
          >
            ▼ 내림
          </button>
          <button
            type="button"
            onClick={() => onSortDir('asc')}
            className={
              'px-2 py-1 ' + (sortDir === 'asc' ? 'bg-gold/15 text-gold' : 'text-gray-400 hover:text-gold')
            }
          >
            ▲ 오름
          </button>
        </div>
      </div>
    </Card>
  );
}

function NumField({
  label,
  value,
  step = 1,
  min,
  max,
  onChange,
}: {
  label: string;
  value: number;
  step?: number;
  min?: number;
  max?: number;
  onChange: (v: number) => void;
}) {
  return (
    <label className="block">
      <span className="text-xs text-gray-400">{label}</span>
      <Input
        type="number"
        className="mt-1"
        value={value}
        step={step}
        min={min}
        max={max}
        onChange={(e) => onChange(Number(e.target.value) || 0)}
      />
    </label>
  );
}

// ─── CSV 다운로드 (Phase 35: 풍부한 컬럼) ───────────────────

function downloadCsv(items: GoldenKeywordItem[], date?: string) {
  // 한글 헤더로 Excel 사용자 친화
  const header = [
    '순위',
    '키워드',
    'GodScore',
    '등급',
    '월총검색량',
    '월PC검색',
    '월모바일검색',
    '블로그문서수',
    '포화도(%)',
    '경쟁도',
    '꿀통점수',
    '기회점수',
    '평균노출수',
    'PC클릭수',
    '모바일클릭수',
    'PC클릭률(%)',
    '모바일클릭률(%)',
    'tier',
    '판정',
    '갱신시각',
  ];
  const fmtNum = (n: number | undefined | null): string => {
    if (n == null || !Number.isFinite(n)) return '';
    return String(n);
  };
  const fmtDate = (unix: number | undefined): string => {
    if (!unix) return '';
    const d = new Date(unix * 1000);
    return d.toISOString().replace('T', ' ').slice(0, 16); // YYYY-MM-DD HH:mm
  };
  const rows = items.map((it, i) => [
    i + 1,
    csvEscape(it.keyword),
    it.godScore,
    it.grade,
    it.totalSearch,
    fmtNum(it.monthlyPc),
    fmtNum(it.monthlyMobile),
    it.blogTotal,
    it.saturation,
    csvEscape(it.compIdx ?? ''),
    fmtNum(it.honeyScore),
    fmtNum(it.opportunity),
    fmtNum(it.plAvgDepth),
    fmtNum(it.monthlyAvePcClk),
    fmtNum(it.monthlyAveMobileClk),
    fmtNum(it.monthlyAvePcCtr),
    fmtNum(it.monthlyAveMobileCtr),
    csvEscape(it.tier ?? ''),
    csvEscape(it.verdict),
    csvEscape(fmtDate(it.refreshedAt)),
  ]);
  const csv = [header.join(','), ...rows.map((r) => r.join(','))].join('\n');
  // BOM (﻿) 추가로 Excel 한글 깨짐 방지
  const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `keywordgod-golden-${date ?? new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

function csvEscape(v: string): string {
  return /[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
}
